import {
  ScreenSpaceEventHandler,
  defined,
  Entity,
  ScreenSpaceEventType,
  Cartesian3,
  HeightReference,
  JulianDate,
  Color,
  Cartographic,
  Math as CesiumMath,
  PolylineDashMaterialProperty,
  PointGraphics,
  ColorMaterialProperty,
  PolylineGlowMaterialProperty,
  PropertyBag,
  ConstantProperty,
  Ellipsoid,
  KeyboardEventModifier,
  BoundingSphere,
} from "cesium";
import { getUsernameAndUserIdFormat, matchKeyInText, showToast } from "./Utility";
import Type from "./Types";
import { toggleLabel } from "./geojson/Label";
import { removeFindClosePointsPrimitive } from "./geojson/FindClosePoints";
import { useAppStore } from "../context/store";

export const handleHighlightMultiGeojson = (viewer) => {
  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);

  const originalStyles = new Map();

  const cloneMaterial = (material) => {
    if (material instanceof ColorMaterialProperty) {
      const color = material.color?.getValue?.() || material.color;
      return new ColorMaterialProperty(Color.clone(color));
    } else if (material instanceof PolylineDashMaterialProperty) {
      const color = material.color?.getValue?.() || material.color;
      return new PolylineDashMaterialProperty({
        color: Color.clone(color),
        dashLength: material.dashLength?.getValue?.() ?? material.dashLength,
        dashPattern: material.dashPattern?.getValue?.() ?? material.dashPattern,
      });
    } else if (material instanceof PolylineGlowMaterialProperty) {
      const color = material.color?.getValue?.() || material.color;
      return new PolylineGlowMaterialProperty({
        color: Color.clone(color),
        glowPower: material.glowPower?.getValue?.() ?? material.glowPower,
        taperPower: material.taperPower?.getValue?.() ?? material.taperPower,
      });
    } else {
      return material;
    }
  };

  const resetStyles = () => {
    for (const [entity, originalMaterial] of originalStyles.entries()) {
      if (entity.polyline) {
        entity.polyline.material = cloneMaterial(originalMaterial);
        entity.polyline.width = new ConstantProperty(2);
      }
    }
    originalStyles.clear();
  };

  handler.setInputAction((click) => {
    resetStyles();

    const pickedObject = viewer.scene.pick(click.position);

    if (defined(pickedObject) && pickedObject.id) {
      const entity = pickedObject.id;

      let ownerDataSource = null;
      viewer.dataSources._dataSources.forEach((ds) => {
        if (ds.entities.contains(entity)) {
          ownerDataSource = ds;
        }
      });
      if (ownerDataSource) {
        if (entity.properties && ownerDataSource.isCenterLane) {
          const now = JulianDate.now();
          const rightLineId = entity.properties["right-line-id"]?.getValue(now);
          const leftLineId = entity.properties["left-line-id"]?.getValue(now);

          let rightLines = [];
          let leftLines = [];

          viewer.dataSources._dataSources.forEach((ds) => {
            const entities = ds.entities.values;

            entities.forEach((e) => {
              if (e.properties && e.polyline) {
                const selectedId = e.properties["line-id"]?.getValue(now);

                if (rightLineId && selectedId == rightLineId) {
                  rightLines.push(e);
                }

                if (leftLineId && selectedId == leftLineId) {
                  leftLines.push(e);
                }
              }
            });
          });

          rightLines.forEach((line, index) => {
            if (!originalStyles.has(line)) {
              originalStyles.set(line, cloneMaterial(line.polyline.material));
            }

            line.polyline.material = new ColorMaterialProperty(rightLines.length > 1 ? Color.ORANGE : Color.BLUE);
            line.polyline.width = new ConstantProperty(5);
          });

          leftLines.forEach((line, index) => {
            if (!originalStyles.has(line)) {
              originalStyles.set(line, cloneMaterial(line.polyline.material));
            }

            line.polyline.material = new ColorMaterialProperty(leftLines.length > 1 ? Color.ORANGE : Color.GREEN);
            line.polyline.width = new ConstantProperty(5);
          });

          if (rightLines.length === 0 && leftLines.length === 0) {
            showToast("No left and right boundary", Type.NOTIFICATION.ERROR);
          } else if (rightLines.length === 0) {
            showToast("Right boundary is not found", Type.NOTIFICATION.WARNING);
          } else if (leftLines.length === 0) {
            showToast("Left boundary is not found", Type.NOTIFICATION.WARNING);
          }

          if (rightLines.length > 1) {
            showToast(`More than one right boundary found (${rightLines.length})`, Type.NOTIFICATION.WARNING);
          }

          if (leftLines.length > 1) {
            showToast(`More than one left boundary found (${leftLines.length})`, Type.NOTIFICATION.WARNING);
          }
        }
      }
    }
  }, ScreenSpaceEventType.LEFT_CLICK);
};

export const toggleRightLaneBoundaryHighlight = (viewer, dataSource, highlight = Color.fromCssColorString("#B266FF").withAlpha(0.85), fallback = Color.GRAY.withAlpha(0.6)) => {
  if (!viewer || !dataSource) return; // safety-check

  /* ------------------------------------------------------------------ */
  /* 1) flip the toggle state                                            */
  /* ------------------------------------------------------------------ */
  const toggled = !dataSource._laneBoundaryToggled;
  dataSource._laneBoundaryToggled = toggled;

  /* ------------------------------------------------------------------ */
  /* 2) Build a fast lookup:  line-id  →  entity                         */
  /* ------------------------------------------------------------------ */
  const idToEntity = {};
  viewer.dataSources._dataSources.forEach((ds) =>
    ds.entities.values.forEach((e) => {
      const id = e.properties?.["line-id"]?.getValue?.() ?? e.properties?.["line-id"];
      if (id) idToEntity[id] = e;
    }),
  );

  /* ------------------------------------------------------------------ */
  /* 3) Iterate over every CENTER LINE in this dataSource                */
  /* ------------------------------------------------------------------ */
  dataSource.entities.values.forEach((center) => {
    if (!dataSource.isCenterLane) return;

    // We only want center-lines whose right_lane flag is true
    const isRightLane =
      String(center.properties?.right_lane?.getValue?.() ?? center.properties?.right_lane)
        .trim()
        .toLowerCase() === "true";
    if (!isRightLane) return;

    // IDs of right / left boundary lines
    const rightId = center.properties?.["right-line-id"]?.getValue?.() ?? center.properties?.["right-line-id"];
    const leftId = center.properties?.["left-line-id"]?.getValue?.() ?? center.properties?.["left-line-id"];

    const rightEnt = idToEntity[rightId];
    const leftEnt = idToEntity[leftId];

    /* ---------------------------------------------------------------- */
    /* 4) Apply or reset style on both boundary polylines               */
    /* ---------------------------------------------------------------- */
    [rightEnt, leftEnt].forEach((edgeEnt) => {
      if (!edgeEnt?.polyline) return;

      // Save original style only once
      if (!edgeEnt.polyline._originalMaterial) {
        edgeEnt.polyline._originalMaterial = edgeEnt.polyline.material;
        edgeEnt.polyline._originalWidth = edgeEnt.polyline.width;
      }

      if (toggled) {
        // ➜  HIGHLIGHT ON
        edgeEnt.polyline.material = new ColorMaterialProperty(highlight);
        edgeEnt.polyline.width = new ConstantProperty(3);
        edgeEnt.polyline._secondaryColor = highlight; // survive external resets
      } else {
        // ➜  HIGHLIGHT OFF  (restore)
        edgeEnt.polyline.material = edgeEnt.polyline._originalMaterial;
        edgeEnt.polyline.width = edgeEnt.polyline._originalWidth ?? new ConstantProperty(2);
        edgeEnt.polyline._secondaryColor = fallback;
      }
    });
  });
};

/**
 * setupPointClickLogger
 * LEFT_CLICK logger for Point / Ellipse / Billboard and circle-like Polygons.
 * - Point / Ellipse: logs entity.position
 * - Polygon (circle-like): logs centroid (via BoundingSphere from vertices)
 * - Billboard: logs entity.position
 * If you want to ignore highlight vertex markers (our white dots), it already does.
 */
export function setupPointClickLogger(viewer, options = {}) {
  const { includeHighlightMarkers = true } = options; // now logs highlight dots too (set false to ignore)
  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);

  /** get current Cesium time */
  const now = () => viewer.clock?.currentTime;

  /** getValue helper for Cesium properties */
  function val(prop) {
    if (!prop) return undefined;
    try {
      return prop.getValue ? prop.getValue(now()) : prop;
    } catch {
      return undefined;
    }
  }

  /** returns true if entity is one of our highlight vertex markers */
  function isHighlightVertexMarker(entity) {
    const props = entity?.properties;
    if (!props) return false;
    const flag = props._isHighlightVertexMarker?.getValue ? props._isHighlightVertexMarker.getValue(now()) : props._isHighlightVertexMarker;
    return flag === true;
  }

  /** gets a representative world position for the picked entity */
  function getRepresentativePosition(ent) {
    // Prefer explicit position if present (Point/Ellipse/Billboard/Model etc.)
    const p = val(ent?.position);
    if (p) return p;

    // If it's a Polygon, compute centroid-ish point from hierarchy vertices
    if (ent?.polygon?.hierarchy) {
      const h = val(ent.polygon.hierarchy);
      if (!h) return null;

      // Flatten PolygonHierarchy to a single array of vertices (outer + holes)
      const verts = [];
      (function flatten(ph) {
        if (!ph) return;
        if (Array.isArray(ph.positions)) verts.push(...ph.positions);
        if (Array.isArray(ph.holes)) ph.holes.forEach(flatten);
      })(h);

      if (verts.length === 0) return null;

      // Use BoundingSphere center of vertices as a robust "center"
      const sphere = BoundingSphere.fromPoints(verts);
      return sphere?.center || null;
    }

    // As a last resort, try the click-to-world (may fail on some primitives/terrain)
    return null;
  }

  /** Main click handler */
  handler.setInputAction((click) => {
    const picked = viewer.scene.pick(click.position);
    if (!picked || !(picked.id instanceof Entity)) return;
    const ent = picked.id;

    // ignore highlight markers
    if (!includeHighlightMarkers && isHighlightVertexMarker(ent)) return;

    const isPointOrCircle = !!(ent.point || ent.ellipse);
    if (!isPointOrCircle) return;

    const pos = getRepresentativePosition(ent);
    if (!pos) {
      const world = viewer.scene.pickPosition?.(click.position);
      if (!world) return;
      logCoords(ent, world);
      return;
    }

    logCoords(ent, pos);
  }, ScreenSpaceEventType.LEFT_CLICK);

  /** Logs both geodetic (deg) and Cartesian coordinates */
  function logCoords(ent, cartesian) {
    const carto = Ellipsoid.WGS84.cartesianToCartographic(cartesian);
    if (!carto) return;

    const lat = CesiumMath.toDegrees(carto.latitude);
    const lon = CesiumMath.toDegrees(carto.longitude);
    const h = carto.height ?? 0;

    showToast(lat.toFixed(7) + " " + lon.toFixed(7) + " " + h.toFixed(2), Type.NOTIFICATION.INFO, { duration: 5000 });
  }

  return handler; // call handler.destroy() when you no longer need it
}

export const handleCursorStyleGeojson = (viewer) => {
  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);

  handler.setInputAction((movement) => {
    const pickedObject = viewer.scene.pick(movement.endPosition);

    if (defined(pickedObject) && pickedObject.id) {
      // viewer.scene.canvas.style.cursor = "pointer"; // Change cursor to pointer when hovering over an entity
    } else {
      // viewer.scene.canvas.style.cursor = "default"; // Reset cursor to default
    }
  }, ScreenSpaceEventType.MOUSE_MOVE);
  return handler;
};

export const updateEntityStyle = (
  dataSource,
  styleWidth,
  styleOutlineColor,
  styleFillColor,
  styleType,
  stylePattern,
  mapObjectsState,
  layerId,
  typeOfGeojson,
  fillPattern = null,
  stylePolygonPattern = "",
  styleInvisibleByDefault = false,
) => {
  const [mapObjects, setMapObjects] = mapObjectsState;

  if (!dataSource) return;

  const color = Color.fromCssColorString(styleOutlineColor);
  const insideColor = Color.fromCssColorString(styleFillColor);
  const lowerStyle = styleType.toLowerCase();

  dataSource.entities.values.forEach((entity) => {
    if (typeOfGeojson === "line" && entity.polyline) {
      if (lowerStyle === "solid") {
        entity.polyline.material = color;
      } else if (lowerStyle === "dashed") {
        entity.polyline.material = new PolylineDashMaterialProperty({
          color,
          dashLength: 16,
        });
      } else if (lowerStyle === "dotted") {
        entity.polyline.material = new PolylineDashMaterialProperty({
          color,
          dashLength: 6,
          dashPattern: 0b00001000100010001,
        });
      } else if (lowerStyle === "pattern" && stylePattern && /^[01]{16}$/.test(stylePattern)) {
        const binaryPattern = parseInt(stylePattern, 2);
        entity.polyline.material = new PolylineDashMaterialProperty({
          color,
          dashLength: 12,
          dashPattern: binaryPattern,
        });
      } else {
        entity.polyline.material = color;
      }

      entity.polyline.width = styleWidth;
      entity.originalColor = color;
      entity.originalWidth = styleWidth;
    }

    if (typeOfGeojson === "polygon" && entity.polygon) {
      if (fillPattern) {
        entity.polygon.material = fillPattern;
      } else {
        entity.polygon.material = insideColor.withAlpha(insideColor.alpha);
      }
      entity.polygon.outline = true;
      entity.polygon.outlineColor = color;
      entity.polygon.outlineWidth = styleWidth;
    }

    if (typeOfGeojson === "point" && entity.point) {
      entity.point.color = color;
      entity.point.outlineColor = color;
      entity.point.pixelSize = styleWidth;
    }
  });

  // after
  setMapObjects((prev) =>
    prev.map((o) =>
      o && o.id === layerId
        ? {
            ...o,
            style_stroke_width: styleWidth,
            style_type: styleType,
            style_pattern: stylePattern,
            style_fill_color: styleFillColor,
            style_outline_color: styleOutlineColor,
            is_edited: true,
            style_fill_pattern: stylePolygonPattern,
            style_invisible_default: styleInvisibleByDefault,
            ...(lowerStyle === "pattern" ? { style_pattern: stylePattern } : {}),
          }
        : o,
    ),
  );
};

export const getAngleFromLine = (p1, p2) => {
  const carto1 = Cartographic.fromCartesian(p1);
  const carto2 = Cartographic.fromCartesian(p2);

  const lat1 = carto1.latitude;
  const lon1 = carto1.longitude;
  const lat2 = carto2.latitude;
  const lon2 = carto2.longitude;

  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const angle = Math.atan2(y, x);

  return angle;
};

export const applyGeojsonStylesToEntities = (viewer, styleMap, mapObjects, setMapObjects) => {
  if (!viewer || !styleMap) return;

  const updatedMapObjects = [...mapObjects];

  for (const key in updatedMapObjects) {
    updatedMapObjects[key].dataSource.entities.values.forEach((entity) => {
      for (const key2 in styleMap) {
        if (matchKeyInText(key2, updatedMapObjects[key].dataSource.layerName)) {
          const config = styleMap[key2];

          // --- Polyline Style ---
          if (entity.polyline) {
            const width = config.defaultLineWidth ?? 2;
            const colorStr = config.defaultOutlineColor || "#ffffff";
            const color = Color.fromCssColorString(colorStr);

            let material = new ColorMaterialProperty(color);

            if (config.defaultStyle.toLowerCase() === "dashed") {
              material = new PolylineDashMaterialProperty({
                color,
                dashLength: 16,
              });
            } else if (config.defaultStyle.toLowerCase() === "dotted") {
              material = new PolylineDashMaterialProperty({
                color,
                dashLength: 5,
              });
            }

            entity.polyline.material = material;
            entity.polyline.width = new ConstantProperty(width);
            entity.polyline._originalMaterial = material;
            entity.polyline._originalWidth = width;
            entity.polyline._secondaryColor = color;
          }

          // --- Polygon Style ---
          if (entity.polygon) {
            const fillColorStr = config.defaultFillColor || "#ffffff00";
            const outlineColorStr = config.defaultOutlineColor || "#000000";

            const fillColor = Color.fromCssColorString(fillColorStr);
            const outlineColor = Color.fromCssColorString(outlineColorStr);

            entity.polygon.material = fillColor;
            entity.polygon.outline = true;
            entity.polygon.outlineColor = outlineColor;

            entity.polygon._originalMaterial = fillColor;
            entity.polygon._originalOutlineColor = outlineColor;
            entity.polygon._secondaryColor = fillColor;
          }

          // --- Point Style ---
          if (!entity.polyline && !entity.polygon) {
            const pointColorStr = config.defaultFillColor || "#ffffff";
            const pointColorStr2 = config.defaultOutlineColor || "#ffffff";
            const pointColor = Color.fromCssColorString(pointColorStr);
            const pointColor2 = Color.fromCssColorString(pointColorStr2);

            entity.point = new PointGraphics({
              color: pointColor,
              pixelSize: config.defaultLineWidth,
              outlineColor: pointColor2,
              outlineWidth: 2,
            });

            if (entity.billboard) {
              entity.billboard = undefined;
            }

            entity._originalColor = pointColor;
            entity._secondaryColor = pointColor;
          }

          updatedMapObjects[key].style_stroke_width = config.defaultLineWidth;
          updatedMapObjects[key].style_fill_color = config.defaultFillColor;
          updatedMapObjects[key].style_outline_color = config.defaultOutlineColor;
          updatedMapObjects[key].style_type = config.defaultStyle;
          updatedMapObjects[key].priority = config.priority;
        }
      }
    });
  }

  const sortedMapObjects = [...updatedMapObjects].sort((a, b) => {
    const pa = Number.isFinite(Number(a?.priority)) ? Number(a.priority) : 0;
    const pb = Number.isFinite(Number(b?.priority)) ? Number(b.priority) : 0;
    return pb - pa; // descending order
  });

  reorderCesiumByPriority(sortedMapObjects, viewer);
  setMapObjects(sortedMapObjects);
  showToast("Applied", Type.NOTIFICATION.SUCCESS);
};

export const reorderCesiumByPriority = (mapObjects, viewer) => {
  // validate inputs quickly
  if (!Array.isArray(mapObjects) || mapObjects.length === 0) return false;
  if (!viewer || !viewer.dataSources || typeof viewer.dataSources.raiseToTop !== "function") {
    // no valid Cesium viewer found
    // eslint-disable-next-line no-console
    console.warn("reorderCesiumByPriority: Cesium viewer not provided or invalid.");
    return false;
  }

  // Make a shallow copy and sort ascending by priority, break ties by _priorityOrder for stability.
  // Sorting ascending + raising to top in order => highest priority ends up on top.
  const sorted = [...mapObjects].sort((a, b) => {
    // ensure numeric priority (fallback 0)
    const pa = Number.isFinite(Number(a?.priority)) ? Number(a.priority) : 0;
    const pb = Number.isFinite(Number(b?.priority)) ? Number(b.priority) : 0;
    if (pa === pb) {
      // stable tie-breaker: use _priorityOrder if present, otherwise fallback to 0
      const oa = Number.isFinite(Number(a?._priorityOrder)) ? Number(a._priorityOrder) : 0;
      const ob = Number.isFinite(Number(b?._priorityOrder)) ? Number(b._priorityOrder) : 0;
      return oa - ob;
    }
    return pa - pb;
  });

  // Iterate the sorted list and raise each dataSource (or array of dataSources) to top.
  // Doing it in ascending order means the last raised (highest priority) sits on top.
  for (const obj of sorted) {
    if (!obj) continue;

    // accept single DataSource or array of DataSources; prefer obj.dataSource
    const dsCandidate = obj.dataSource ?? obj.osmDataSource ?? null;
    if (!dsCandidate) continue;

    // helper inline: raise a single datasource safely
    const raiseSingle = (ds) => {
      try {
        // Cesium's raiseToTop moves given datasource to render on top
        viewer.dataSources.raiseToTop(ds);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("reorderCesiumByPriority: failed to raise dataSource for mapObject id:", obj.id, err);
      }
    };

    // if it's an array-like, iterate, else raise single
    if (Array.isArray(dsCandidate)) {
      for (const d of dsCandidate) {
        if (d) raiseSingle(d);
      }
    } else {
      raiseSingle(dsCandidate);
    }
  }

  return true;
};

export const updateNestedKey = (obj, key, value, visited = new Set()) => {
  if (typeof obj !== "object" || obj === null) return;

  if (visited.has(obj)) return;
  visited.add(obj);

  for (const k in obj) {
    if (k === key) {
      obj[k] = value;
    } else if (typeof obj[k] === "object" && obj[k] !== null) {
      updateNestedKey(obj[k], key, value, visited);
    }
  }
};

export const updateDatasourceProperty = (datasource, key, value, allowedIds) => {
  const now = JulianDate.now();

  if (!datasource || !Array.isArray(datasource.entities?.values)) {
    console.warn("Invalid datasource");
    return;
  }

  datasource.entities.values.forEach((entity) => {
    if (entity.properties && typeof entity.properties.getValue === "function") {
      const props = entity.properties.getValue(now);

      if (props.feature_id && allowedIds.includes(props.feature_id)) {
        updateNestedKey(props, key, value);
        entity.properties = new PropertyBag(props);
      }
    }
  });
};

export const updateEntitiesProperty = (entities, key, value, setMapObjects, userData) => {
  if (!Array.isArray(entities)) return;

  const now = JulianDate.now();
  const attEditor = getUsernameAndUserIdFormat(userData);

  const touchedLayers = new Set();

  entities.forEach((entity) => {
    if (!entity) return;

    // correct way to get dataSource of entity in Cesium
    const collection = entity.entityCollection || entity._entityCollection || (entity._source && entity._source._entityCollection) || null;

    // Owner is usually the dataSource
    const dataSource = collection && collection.owner ? collection.owner : null;
    const layerId = dataSource?.layerId;

    if (layerId) {
      touchedLayers.add(layerId);
    } else {
    }

    const props = entity.properties;

    if (!props) {
      entity.properties = new PropertyBag({
        [key]: value,
        ...(attEditor ? { Att_editor: attEditor } : {}),
      });
      return;
    }

    if (typeof props.getValue === "function") {
      const current = props.getValue(now) || {};
      current[key] = value;
      if (attEditor) current.Att_editor = attEditor;
      entity.properties = new PropertyBag(current);
      return;
    }

    props[key] = value;
    if (attEditor) props.Att_editor = attEditor;
  });

  setMapObjects((prev) => prev.map((obj) => (obj && touchedLayers.has(obj.id) ? { ...obj, is_edited: true } : obj)));
};

export const removeCenterlineQuadsLayer = (viewerRef) => {
  if (!viewerRef) return;

  const dsCollection = viewerRef.dataSources;
  const len = dsCollection.length;

  for (let i = len - 1; i >= 0; i--) {
    const ds = dsCollection.get(i);
    if ((ds?.layerName ?? ds?.name) === "centerline_quads") {
      dsCollection.remove(ds, true);
    }
  }
};

export const enableAltHoverVertexPreview = (
  viewer,
  {
    onPointContextClick,
    shouldEnable = () => true,
    markerStyle = {},
    includePolygons = true,
    includePolylines = true,
    includePoints = true,
    preventBrowserContextMenu = false, // optional: block default browser menu
  } = {},
) => {
  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);

  // --- Internal state --------------------------------------------------------------------------
  let currentEntityId = null; // currently previewed entity id
  const markerPool = []; // reusable markers
  const helperFlagKey = "isAltCoordMarker"; // property flag for helper markers

  // --- Marker style (soft cyan) ----------------------------------------------------------------
  const { color = Color.fromCssColorString("#00e5ff").withAlpha(0.95), pixelSize = 10, outlineColor = Color.WHITE.withAlpha(0.9), outlineWidth = 1.5 } = markerStyle;

  // Optionally prevent default browser context menu on right click over the canvas
  let preventCtx;
  if (preventBrowserContextMenu) {
    preventCtx = (e) => e.preventDefault();
    viewer.canvas.addEventListener("contextmenu", preventCtx);
  }

  // =============================== Utilities ====================================================

  /** Safely read a PropertyBag entry (ConstantProperty or raw value). */
  function propValue(prop) {
    // If it's a ConstantProperty or other property-like object, read via getValue(now)
    const now = JulianDate.now();
    return prop?.getValue ? prop.getValue(now) : prop;
  }

  /** Get positions (Cartesian3[]) from entity; strip closing duplicate for polygons. */
  function getPositionsFromEntity(entity) {
    if (includePolylines && entity?.polyline) {
      const arr = entity.polyline.positions?.getValue?.(JulianDate.now()) ?? entity.polyline.positions?.getValue?.();
      return Array.isArray(arr) ? arr : [];
    }
    if (includePolygons && entity?.polygon) {
      const raw = entity.polygon.hierarchy?.getValue?.(JulianDate.now())?.positions ?? entity.polygon.hierarchy?.getValue?.()?.positions ?? [];
      const positions = [...raw];
      if (positions.length > 2 && Cartesian3.equals(positions[0], positions[positions.length - 1])) {
        positions.pop(); // remove duplicate closing vertex
      }
      return positions;
    }
    if (includePoints && entity?.position) {
      const p = entity.position?.getValue?.(JulianDate.now()) ?? entity.position?.getValue?.();
      return p ? [p] : [];
    }
    return [];
  }

  /** Create one hidden marker entity (with PropertyBag flags) and push to pool. */
  function createMarker() {
    const ent = viewer.entities.add({
      position: Cartesian3.fromDegrees(0, 0, 0),
      point: {
        color,
        pixelSize,
        heightReference: HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        outlineColor,
        outlineWidth,
      },
      show: false,
      // Use PropertyBag so .properties[key].getValue() works
      properties: new PropertyBag({
        [helperFlagKey]: true,
        parentEntityId: null,
        pointIndex: -1,
      }),
    });
    markerPool.push(ent);
    return ent;
  }

  /** Hide all markers (keep them for reuse). */
  function hideAllMarkers() {
    for (const m of markerPool) m.show = false;
  }

  /** Ensure pool has at least n markers. */
  function ensurePoolSize(n) {
    while (markerPool.length < n) createMarker();
  }

  /** Show markers for an entity's vertices. */
  function showMarkersForEntity(entity) {
    const positions = getPositionsFromEntity(entity);
    if (!positions.length) {
      hideAllMarkers();
      currentEntityId = null;
      return;
    }

    ensurePoolSize(positions.length);

    for (let i = 0; i < markerPool.length; i++) {
      const marker = markerPool[i];
      if (i < positions.length) {
        marker.position = positions[i]; // set world position

        // Assign PropertyBag fields; Cesium wraps into ConstantProperty internally
        marker.properties.parentEntityId = entity.id; // parent id
        marker.properties.pointIndex = i; // vertex index

        marker.show = true;
      } else {
        marker.show = false;
      }
    }
    currentEntityId = entity.id;
  }

  // =============================== Event wiring =================================================

  // ALT + mouse move → update markers for entity under cursor
  handler.setInputAction(
    (movement) => {
      if (!shouldEnable()) {
        hideAllMarkers();
        return;
      }
      const picked = viewer.scene.pick(movement.endPosition);

      // If hovering a helper marker, keep the preview (avoid flicker)
      const isHelper = propValue(picked?.id?.properties?.[helperFlagKey]) === true;
      if (isHelper) return;

      if (defined(picked) && picked.id instanceof Entity) {
        showMarkersForEntity(picked.id);
      } else {
        hideAllMarkers();
        currentEntityId = null;
      }
    },
    ScreenSpaceEventType.MOUSE_MOVE,
    KeyboardEventModifier.ALT,
  );

  // Mouse move without ALT → hide markers
  handler.setInputAction(() => {
    hideAllMarkers();
    currentEntityId = null;
  }, ScreenSpaceEventType.MOUSE_MOVE);

  /** Common handler for RIGHT_CLICK (with or without ALT). */
  function handleRightClick(click) {
    if (!shouldEnable()) return;

    const picked = viewer.scene.pick(click.position);
    // IMPORTANT: read PropertyBag value via propValue()
    const isMarker = propValue(picked?.id?.properties?.[helperFlagKey]) === true;
    if (!isMarker || typeof onPointContextClick !== "function") return;

    const marker = picked.id;

    // Read PropertyBag values safely
    const parentId = propValue(marker.properties?.parentEntityId);
    const index = propValue(marker.properties?.pointIndex);
    const position = marker.position?.getValue?.(JulianDate.now()) ?? marker.position;

    // Resolve parent entity (optional convenience)
    let parentEntity = viewer.entities.getById?.(parentId) || null;
    if (!parentEntity && viewer.dataSources?._dataSources) {
      for (const ds of viewer.dataSources._dataSources) {
        const e = ds.entities.getById?.(parentId);
        if (e) {
          parentEntity = e;
          break;
        }
      }
    }

    onPointContextClick({
      entity: parentEntity,
      parentId,
      index,
      position,
      screenPosition: click.position,
    });
  }

  // RIGHT_CLICK (no modifier)
  handler.setInputAction(handleRightClick, ScreenSpaceEventType.RIGHT_CLICK);
  // RIGHT_CLICK + ALT (defensive: some setups bind with modifier)
  handler.setInputAction(handleRightClick, ScreenSpaceEventType.RIGHT_CLICK, KeyboardEventModifier.ALT);

  // ALT key released → hide markers
  const onKeyUp = (e) => {
    if (e.key === "Alt") {
      hideAllMarkers();
      currentEntityId = null;
    }
  };
  window.addEventListener("keyup", onKeyUp);

  // =============================== Teardown =====================================================
  function destroy() {
    try {
      handler.destroy();
    } catch (_) {}
    window.removeEventListener("keyup", onKeyUp);
    if (preventCtx) viewer.canvas.removeEventListener("contextmenu", preventCtx);
    for (const m of markerPool) viewer.entities.remove(m);
    markerPool.length = 0;
    currentEntityId = null;
  }

  return { destroy };
};

/**
 * Delete an asset (dataSource, entities, primitives, etc.) from the Cesium viewer.
 * - Cleans up related billboards, arrows, OSM layers, circles, inner entities.
 * - Also clears endpoint highlight circles if they exist.
 */
export const deleteAsset = (viewer, item, mapObjectsState, setImageGeojsonMode, setCamera) => {
  try {
    toggleLabel({ viewer, clearTextCache: true, removeAll: true, dataSource: item.dataSource });
  } catch (e) {
    console.error("deleteAsset toggleLabel", e);
  }

  const [mapObjects, setMapObjects] = mapObjectsState;
  const objectToRemove = mapObjects.find((obj) => obj.id === item.id);
  if (!objectToRemove) return;

  // Data source cleanup
  const ds = objectToRemove.dataSource;
  if (ds) {
    try {
      if (ds._arrowUpdater) {
        viewer.scene.postRender.removeEventListener(ds._arrowUpdater);
        ds._arrowUpdater = null;
      }
      if (ds._arrowCameraListener) {
        viewer.camera.changed.removeEventListener(ds._arrowCameraListener);
        ds._arrowCameraListener = null;
      }
    } catch (e) {
      console.error("deleteAsset arrow listeners", e);
    }

    try {
      if (ds._arrowBillboards) {
        ds._arrowBillboards.removeAll();
        if (viewer.scene.primitives.contains(ds._arrowBillboards)) {
          viewer.scene.primitives.remove(ds._arrowBillboards);
        }
        ds._arrowBillboards = null;
      }
    } catch (e) {
      console.error("deleteAsset arrow billboards", e);
    }

    try {
      if (viewer.dataSources.contains(ds)) {
        viewer.dataSources.remove(ds, true);
      }
    } catch (e) {
      console.error("deleteAsset dataSource remove", e);
    }

    try {
      removeFindClosePointsPrimitive(viewer, ds);
    } catch (e) {
      console.error("deleteAsset removeFindClosePointsPrimitive", e);
    }
  }

  // Tileset cleanup
  if (objectToRemove.tileset) {
    try {
      if (viewer.scene.primitives.contains(objectToRemove.tileset)) {
        viewer.scene.primitives.remove(objectToRemove.tileset);
      }
    } catch (e) {
      console.error("deleteAsset tileset", e);
    }
    viewer.scene.skyBox.show = true;
    viewer.scene.skyAtmosphere.show = true;
    viewer.scene.globe.show = true;
    viewer.scene.backgroundColor = Color.BLACK;
  }

  // OSM cleanup
  if (objectToRemove.osmDataSource) {
    try {
      viewer.dataSources.remove(objectToRemove.osmDataSource, true);
    } catch (e) {
      console.error("deleteAsset osmDataSource", e);
    }
  }

  if (objectToRemove.laneletDataSource) {
    try {
      viewer.dataSources.remove(objectToRemove.laneletDataSource, true);
    } catch (e) {
      console.error("deleteAsset laneletDataSource", e);
    }
  }

  // Point collection cleanup
  if (objectToRemove.pointCollection) {
    try {
      if (viewer.scene.primitives.contains(objectToRemove.pointCollection)) {
        viewer.scene.primitives.remove(objectToRemove.pointCollection);
      }
    } catch (e) {
      console.error("deleteAsset pointCollection", e);
    }
  }

  if (objectToRemove.type === "CIRCLE" && objectToRemove.dataSourceId) {
    try {
      const dsCircle = objectToRemove.dataSourceId;
      if (dsCircle && viewer.dataSources.contains(dsCircle)) {
        viewer.dataSources.remove(dsCircle, true);
      }
    } catch (e) {
      console.error("deleteAsset circle dataSource", e);
    }
  }

  // Entity cleanup
  if (objectToRemove.entities?.length > 0) {
    for (const entity of objectToRemove.entities) {
      try {
        viewer.entities.remove(entity);
      } catch (e) {
        console.error("deleteAsset entities", e);
      }
    }
  }

  // Inner entity cleanup
  if (objectToRemove.inner_entities?.length > 0) {
    for (const innerItem of objectToRemove.inner_entities) {
      for (const entity of innerItem.entities || []) {
        try {
          viewer.entities.remove(entity);
        } catch (e) {
          console.error("deleteAsset inner_entities", e);
        }
      }
    }
  }

  // GeoJSON image special case
  if (objectToRemove.type === Type.ASSET.GEOJSON_IMAGE) {
    setImageGeojsonMode(false);
    setCamera(null);
  }

  // Imagery cleanup ✅ (AERIAL IMAGERY)
  if (objectToRemove.imageryLayer) {
    try {
      objectToRemove.imageryLayer.__aerialDispose?.();
    } catch (e) {
      console.error("deleteAsset imageryLayer dispose", e);
    }

    try {
      if (viewer?.imageryLayers) {
        viewer.imageryLayers.remove(objectToRemove.imageryLayer, true);
      }
    } catch (e) {
      console.error("deleteAsset imageryLayer remove", e);
    }
  }

  // Update state
  setMapObjects((prev) => prev.filter((obj) => obj && obj.id !== item.id));
};

export const getEntityPlainProps = (entity, when = JulianDate.now()) => {
  try {
    return entity?.properties?.getValue?.(when) || {};
  } catch {
    return {};
  }
};

export const collectCandidateIds = (propsObj) => {
  const ids = new Set();
  const v = (k) => propsObj?.[k];
  ["feature_id", "id", "featureId", "fid"].forEach((k) => {
    const val = v(k);
    if (val !== undefined && val !== null && val !== "") ids.add(String(val));
  });
  return ids;
};

export const removeFromFeatureCollectionByAnyId = (fc, candidateIds) => {
  if (!fc || fc.type !== "FeatureCollection" || !Array.isArray(fc.features)) return fc;

  const keep = fc.features.filter((f) => {
    const p = f?.properties || {};
    const vals = [p.feature_id, p.id, p.featureId, p.fid].map((x) => (x === undefined || x === null ? undefined : String(x)));

    // keep if none of the values are in candidateIds
    return !vals.some((val) => val && candidateIds.has(val));
  });

  // If nothing changed, keep the original reference
  if (keep.length === fc.features.length) return fc;

  return { ...fc, features: keep };
};

export const updateLayerAndMapObjectsGeoJSON = (layerId, nextGeoJSON, setCurrentLayerForAttributeTable, setMapObjects) => {
  // update currentLayer (if you keep it separately in state)
  setCurrentLayerForAttributeTable((prev) => {
    if (!prev) return prev;
    if (prev.id !== layerId) return prev;
    return { ...prev, geojson: nextGeoJSON };
  });

  // update mapObjects array immutably
  setMapObjects((prev) => prev.map((mo) => (mo && mo.id === layerId ? { ...mo, geojson: nextGeoJSON } : mo)));
};
